import React, { useCallback, useEffect, useMemo, useState } from 'react';
import MasterLayout from '../../masterLayout/MasterLayout';
import Breadcrumb from '../../components/Breadcrumb';
import { notify } from '../../components/common/AppToast';
import KkpDistribCitiesMap, { groupDistribCities } from '../../components/kkp/KkpDistribCitiesMap';
import kkpDistribService from '../../services/kkpDistribService';
import '../../styles/kkp-planning-page.scss';
import '../../styles/kkp-distrib-page.scss';

const KkpDistribCarteVillesPage = () => {
  const [zones, setZones] = useState([]);
  const [points, setPoints] = useState([]);
  const [activeZones, setActiveZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [citySupport, setCitySupport] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [meta, listRes] = await Promise.all([
        kkpDistribService.getMeta(),
        kkpDistribService.listPoints({ limit: 1000 }),
      ]);
      const nextZones = meta?.zones ?? [];
      setZones(nextZones);
      setPoints(listRes.points);
      setActiveZones(nextZones.map((z) => z.code || z.key));
    } catch (err) {
      notify.error(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const visiblePoints = useMemo(
    () => points.filter((p) => activeZones.includes(p.zone)),
    [points, activeZones],
  );

  const allCities = useMemo(() => groupDistribCities(visiblePoints), [visiblePoints]);

  const cityMarkers = useMemo(() => {
    if (citySupport === 'mag') return allCities.filter((c) => c.hasMag);
    if (citySupport === 'set') return allCities.filter((c) => c.hasSet);
    return allCities;
  }, [allCities, citySupport]);

  const cityCounts = useMemo(() => ({
    all: allCities.length,
    mag: allCities.filter((c) => c.hasMag).length,
    set: allCities.filter((c) => c.hasSet).length,
  }), [allCities]);

  const toggleZone = (code) => {
    setActiveZones((prev) => (
      prev.includes(code) ? prev.filter((z) => z !== code) : [...prev, code]
    ));
  };

  return (
    <MasterLayout>
      <div className="kkp-distrib-villes-page">
        <Breadcrumb
          title="Carte des villes"
          subtitle={loading
            ? 'Chargement…'
            : `${cityMarkers.length} ville(s) · MAG et/ou SET déposés`}
        />

        <div className="kkp-distrib-villes-page__toolbar">
          <div className="kkp-distrib-map-toolbar">
            {zones.map((zone) => {
              const code = zone.code || zone.key;
              const active = activeZones.includes(code);
              return (
                <button
                  key={code}
                  type="button"
                  className={`kkp-distrib-map-chip ${active ? 'is-active' : ''}`}
                  style={{ '--zone-color': zone.couleur || '#64748b' }}
                  onClick={() => toggleZone(code)}
                >
                  <span className="kkp-distrib-zone-dot" style={{ background: zone.couleur }} />
                  {zone.label}
                  <small>{zone.livreur || ''}</small>
                </button>
              );
            })}
          </div>
          <div className="kkp-distrib-map-toolbar">
            {[
              { key: 'all', label: `Tous (${cityCounts.all})` },
              { key: 'mag', label: `Mag (${cityCounts.mag})` },
              { key: 'set', label: `Set (${cityCounts.set})` },
            ].map((opt) => (
              <button
                key={opt.key}
                type="button"
                className={`kkp-distrib-map-chip ${citySupport === opt.key ? 'is-active' : ''}`}
                style={{
                  '--zone-color': opt.key === 'mag' ? '#be123c' : opt.key === 'set' ? '#2563eb' : '#334155',
                }}
                onClick={() => setCitySupport(opt.key)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="kkp-distrib-villes-page__map">
          <KkpDistribCitiesMap cities={cityMarkers} fill />
        </div>
      </div>
    </MasterLayout>
  );
};

export default KkpDistribCarteVillesPage;
